import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { PpScreen } from './PpScreen';
import { useDispositionPalette } from './dispositionPalette';
import IntegrationApplicationMasterData from './screens/IntegrationApplicationMasterData';
import IntegrationGroupings from './screens/IntegrationGroupings';
import PhaseEffortPlanning from './screens/PhaseEffortPlanning';
import ApplicationDependencies from './screens/ApplicationDependencies';
import ResourceSkillsets from './screens/ResourceSkillsets';
import ResourceCapacity from './screens/ResourceCapacity';
import ResourceAllocation from './screens/ResourceAllocation';
import RoleUtilization from './screens/RoleUtilization';
import DispositionControl from './screens/DispositionControl';
import RoadmapSchedule from './screens/RoadmapSchedule';
import ImplementationRoadmap from './screens/ImplementationRoadmap';
import Dashboard from './screens/Dashboard';

/**
 * Program Planning & Roadmap — apr_template's Planning & Integration menu as a product.
 *
 * Same shape as `pages/apr/AprModule.tsx` (cloned, not imported): the route is a wildcard,
 * the position is resolved against the catalogue, and each screen is registered at the
 * path the catalogue gives it. Screens are the source's own (`./screens/`), each rendered
 * inside `PpScreen` for the scoped source stylesheet.
 */
const SCREENS: Record<string, () => JSX.Element> = {
  'grouping-scope/integration-app-master-data': () => <IntegrationApplicationMasterData />,
  'grouping-scope/integration-groupings': () => <IntegrationGroupings />,
  'grouping-scope/phase-effort-planning': () => <PhaseEffortPlanning />,
  'grouping-scope/application-dependencies': () => <ApplicationDependencies />,
  'resource-planning/resource-skillsets': () => <ResourceSkillsets />,
  'resource-planning/resource-capacity': () => <ResourceCapacity />,
  'resource-planning/resource-allocation': () => <ResourceAllocation />,
  'resource-planning/role-utilization': () => <RoleUtilization />,
  'roadmap-planning/roadmap-approval': () => <DispositionControl />,
  'roadmap-planning/roadmap-schedule': () => <RoadmapSchedule />,
  'roadmap-planning/high-level-implementation-roadmap': () => <ImplementationRoadmap />,
  'executive-dashboard': () => <Dashboard />,
};

/**
 * The source's `/integration-framework` redirected to Integration App Master Data
 * (App.tsx:390) — the screen where transferred applications first appear. The module
 * root does the same.
 */
const LANDING = '/program_planning/grouping-scope/integration-app-master-data';

export interface ProgramPlanningModuleProps {
  modules: PortalModule[];
}

export function ProgramPlanningModule({ modules }: ProgramPlanningModuleProps) {
  const module = modules.find((m) => m.id === 'program_planning');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;
  return <ProgramPlanningContent modules={modules} module={module} />;
}

function ProgramPlanningContent({
  modules,
  module,
}: {
  modules: PortalModule[];
  module: PortalModule;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  // The cloned screens colour rows through plain helpers that read the Disposition Matrix
  // palette (§1.9.3 rule 3). Subscribing HERE means the screen element is re-created when the
  // matrix arrives, so the screen repaints in place — a subscription inside PpScreen would
  // not reach it, because React skips an unchanged `children` element.
  useDispositionPalette();
  const at = resolveNavigation(location.pathname, [module]);

  if (at.trail.length === 0) return <Navigate to={LANDING} replace />;

  const deepest = at.trail[at.trail.length - 1]!;
  if (!at.section) {
    return (
      <SectionIndex
        title={deepest.label}
        sections={deepest.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const render = SCREENS[at.trail.map((s) => s.key).join('/')];
  if (!render) {
    return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={{ key: at.section.key, label: at.section.label }}
      />
    );
  }

  return <PpScreen>{render()}</PpScreen>;
}
